import { Link } from "react-router-dom";
import { Palmtree, ArrowRight } from "lucide-react";
import { CITIES } from "@/cityConfig";

// "Nearby in Asia" cross-link block linking Colombo with the Thai trio (Bangkok/Phuket/Pattaya).
// Renders on these four Asian pages and links to the other three destinations.
const ASIA_CITIES = ["colombo", "bangkok", "phuket", "pattaya"];

const PARTNERS = {
  colombo: ["bangkok", "phuket", "pattaya"],
  bangkok: ["colombo", "phuket", "pattaya"],
  phuket: ["colombo", "bangkok", "pattaya"],
  pattaya: ["colombo", "bangkok", "phuket"],
};

const COPY = {
  colombo: {
    ru: "Заказываете цветы в Азии? Мы доставляем не только в Коломбо, но и на популярных курортах региона. Планируете поездку по Азии или отправляете сюрприз близким сразу в нескольких городах — свежие букеты привезём и в Бангкоке, и на Пхукете, и в Паттайе.",
    en: "Ordering flowers in Asia? We deliver not only in Colombo but at the region's popular resorts too. Travelling around Asia, or sending a surprise to several cities at once — we bring fresh bouquets to Bangkok, Phuket and Pattaya as well.",
  },
  bangkok: {
    ru: "Доставку мы делаем по всей Азии, не только в Бангкоке. Летите дальше на острова или отправляете цветы близким в разных странах региона? Свежие букеты привезём и в Коломбо на Шри-Ланке, и на Пхукете, и в Паттайе.",
    en: "We deliver across Asia, not just in Bangkok. Flying on to the islands or sending flowers to loved ones across the region? We'll bring fresh bouquets to Colombo in Sri Lanka, to Phuket and to Pattaya too.",
  },
  phuket: {
    ru: "Пхукет — жемчужина юга Таиланда, но доставку мы организуем по всей Азии. Едете дальше по региону или готовите сюрприз сразу в нескольких городах? Свежие букеты привезём и в Бангкоке, и в Паттайе, и в Коломбо на Шри-Ланке.",
    en: "Phuket is the jewel of southern Thailand, but we arrange delivery across all of Asia. Travelling on around the region, or planning a surprise in several cities? We'll bring fresh bouquets to Bangkok, Pattaya and Colombo in Sri Lanka too.",
  },
  pattaya: {
    ru: "Паттайя — популярный курорт Таиланда, но доставку мы делаем по всей Азии. Путешествуете по региону или отправляете цветы близким в разных странах? Свежие букеты привезём и в Бангкоке, и на Пхукете, и в Коломбо на Шри-Ланке.",
    en: "Pattaya is a popular Thai resort, but we deliver across all of Asia. Travelling around the region or sending flowers to loved ones in different countries? We'll bring fresh bouquets to Bangkok, Phuket and Colombo in Sri Lanka too.",
  },
};

const HEADING = { ru: "Рядом в Азии", en: "Nearby in Asia" };

export const AsiaNearbyBlock = ({ citySlug, lang = "ru" }) => {
  if (!ASIA_CITIES.includes(citySlug)) return null;
  const partners = (PARTNERS[citySlug] || []).map((s) => CITIES[s]).filter(Boolean);
  if (partners.length === 0) return null;

  const l = lang === "en" ? "en" : "ru";
  const body = (COPY[citySlug] || {})[l];
  const langSuffix = lang !== "ru" ? `?lang=${lang}` : "";

  const anchor = (c) =>
    l === "en"
      ? `Flower delivery ${c.namePrepositionalEn || `in ${c.nameEn || c.name}`}`
      : `Доставка цветов ${c.namePrepositional || `в ${c.name}`}`;

  return (
    <section className="py-12 sm:py-16 bg-[#0c332b]" data-testid="asia-nearby-block">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 sm:p-10">
          <div className="flex items-center gap-2 text-emerald-300 mb-4">
            <Palmtree className="w-5 h-5" />
            <span className="text-xs sm:text-sm font-semibold tracking-widest uppercase">
              {HEADING[l]}
            </span>
          </div>
          <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-6">{body}</p>
          <div className="flex flex-wrap gap-3">
            {partners.map((c) => (
              <Link
                key={c.slug}
                to={`/${c.slug}${langSuffix}`}
                className="group inline-flex items-center gap-2 rounded-full bg-white text-[#0c332b] px-6 py-3 text-sm sm:text-base font-semibold hover:bg-emerald-200 transition-colors"
                data-testid={`asia-partner-link-${c.slug}`}
              >
                {anchor(c)}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AsiaNearbyBlock;
